import React from 'react';
import propTypes from 'prop-types';
import { CircleCheck, CircleX } from 'lucide-react';
import Modal from 'tailwind-components/Modal/Modal';
import Button from 'tailwind-components/Button/Button';

function OnboardingBulkCancelModal({
  open,
  phase,
  records,
  results,
  onConfirm,
  onClose
}) {
  const count = records.length;

  if (phase === 'running') {
    return (
      <Modal
        active={open}
        closeModal={() => {}}
        title='Cancelling...'
        size='large'
        isLoading
        hideFooter
      />
    );
  }

  if (phase === 'results') {
    const succeeded = results.filter(r => r.ok).length;
    const failed = results.length - succeeded;
    return (
      <Modal
        active={open}
        closeModal={onClose}
        title='Bulk cancel results'
        size='large'
        showScrollbar
        footer={
          <div className='flex w-full justify-end'>
            <Button
              variant='primary'
              size='small'
              text='Done'
              onClick={onClose}
            />
          </div>
        }
      >
        <div className='flex flex-col gap-3 text-left'>
          <p className='m-0 text-sm text-grey'>
            <span className='font-semibold text-black'>{succeeded}</span>{' '}
            succeeded
            {failed > 0 && (
              <>
                {', '}
                <span className='font-semibold text-semantic-danger'>
                  {failed}
                </span>{' '}
                failed
              </>
            )}
          </p>
          <ul className='m-0 flex list-none flex-col gap-1.5 p-0'>
            {results.map(r => (
              <li
                key={r.id}
                className='flex items-start gap-2 rounded-md border border-grey-lighter px-3 py-2 text-sm'
              >
                {r.ok ? (
                  <CircleCheck
                    className='mt-0.5 size-4 shrink-0 text-secondary-darkened'
                    aria-hidden
                  />
                ) : (
                  <CircleX
                    className='mt-0.5 size-4 shrink-0 text-semantic-danger'
                    aria-hidden
                  />
                )}
                <div className='min-w-0'>
                  <span className='font-semibold text-black'>
                    {r.agentName || '—'}
                  </span>
                  <span className='ml-1 text-grey'>NPN {r.npn || '—'}</span>
                  {r.agencyName && (
                    <span className='ml-1 text-grey'>— {r.agencyName}</span>
                  )}
                  {!r.ok && (
                    <p className='m-0 mt-0.5 text-xs text-semantic-danger'>
                      Failed to cancel onboarding
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      active={open}
      closeModal={onClose}
      title={`Cancel onboarding for ${count} agent${count !== 1 ? 's' : ''}?`}
      size='large'
      showScrollbar
      footer={
        <div className='flex w-full flex-wrap justify-end gap-2'>
          <Button
            variant='secondary'
            size='small'
            text='Go back'
            onClick={onClose}
          />
          <Button
            variant='danger'
            size='small'
            text={`Cancel ${count} onboarding${count !== 1 ? 's' : ''}`}
            onClick={onConfirm}
          />
        </div>
      }
    >
      <div className='flex flex-col gap-3 text-left'>
        <p className='m-0 rounded-md border border-grey-lighter bg-grey-lightest px-3 py-2 text-sm font-medium text-black'>
          This action cannot be undone. The selected agents will be removed from
          the current onboarding flow in this dashboard.
        </p>
        <ul className='m-0 flex list-none flex-col gap-1.5 p-0'>
          {records.map(r => (
            <li
              key={`${r.agentAgencyId ?? r.agentId ?? ''}-${r.npn}`}
              className='rounded-md border border-grey-lighter px-3 py-2 text-sm'
            >
              <span className='font-semibold text-black'>
                {r.agentName || '—'}
              </span>
              <span className='ml-1 text-grey'>NPN {r.npn || '—'}</span>
              {r.relatedToAgency && (
                <span className='ml-1 text-grey'>— {r.relatedToAgency}</span>
              )}
              {r.groupLabel && (
                <span className='ml-1 text-xs text-grey'>({r.groupLabel})</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

OnboardingBulkCancelModal.propTypes = {
  open: propTypes.bool.isRequired,
  phase: propTypes.oneOf(['confirm', 'running', 'results']).isRequired,
  records: propTypes.array.isRequired,
  results: propTypes.array.isRequired,
  onConfirm: propTypes.func.isRequired,
  onClose: propTypes.func.isRequired
};

export default OnboardingBulkCancelModal;
